// View-Model-Helfer für Wartungen (Kampagnen) und Wartungsverträge.
// Wird von der Übersicht, der Vertragsdetail-Seite und der Wartungshülle geteilt,
// damit Labels, Links und Status überall identisch aussehen.

import {
  contractLocationLabel,
  isOnCallContract,
  maintenanceContractCustomerLabel,
  type MaintenanceCampaign,
  type MaintenanceContract,
} from "@/lib/wartungen";
import { projectWorkflowStatus, type ProjectWorkflowStatusView } from "@/lib/projects";
import { durationLabel, frequencyLabel, toPeriod } from "./contractPeriod";
import { formatDate } from "./wartungenUi";

export const FILTER_ALL = "__all__";

export interface OptionEntry {
  key: string;
  label: string;
}

export interface CampaignLocationInfo extends OptionEntry {
  heroAddressId: number | null;
  subLocationIds: number[];
}

export interface CampaignView {
  id: string;
  campaignId: number | null;
  contract: MaintenanceContract;
  campaign: MaintenanceCampaign;
  title: string;
  customer: OptionEntry;
  location: CampaignLocationInfo;
  category: OptionEntry;
  year: string;
  scheduleLabel: string;
  workflow: ProjectWorkflowStatusView;
  contractLabel: string;
  /** „Vertrag“ oder „Auf Zuruf“ – Beschriftung der Herkunft, nicht der Titel. */
  contractRelationLabel: string;
  href: string | null;
}

export function campaignYear(c: MaintenanceCampaign): string {
  const explicit = String(c.year || "").trim();
  if (/^\d{4}$/.test(explicit)) return explicit;
  const m = String(c.date_value || "").trim().match(/^(\d{4})/);
  return m?.[1] ?? "–";
}

const MONTHS_DE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

/**
 * Lesbarer Zeitraum einer Wartung, abhängig vom Datumsmodus:
 * day → 24.03.2026, month → März 2026, year → 2026.
 */
export function formatSchedule(mode?: string | null, value?: string | null): string {
  const v = String(value || "").trim();
  if (!v) return "–";
  const m = String(mode || "").trim().toLowerCase();
  const yearMatch = v.match(/^(\d{4})/);
  if (m === "year" || /^\d{4}$/.test(v)) return yearMatch?.[1] ?? v;
  const monthMatch = v.match(/^(\d{4})-(\d{2})$/);
  if (m === "month" || monthMatch) {
    const mm = monthMatch ?? v.match(/^(\d{4})-(\d{2})/);
    if (mm) {
      const idx = Number(mm[2]) - 1;
      return `${MONTHS_DE[idx] ?? mm[2]} ${mm[1]}`;
    }
  }
  const dayMatch = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dayMatch) return `${dayMatch[3]}.${dayMatch[2]}.${dayMatch[1]}`;
  return v;
}

export function scheduleModeLabel(mode?: string | null): string {
  switch (String(mode || "").trim().toLowerCase()) {
    case "day":
      return "Tag";
    case "month":
      return "Monat";
    case "year":
      return "Jahr";
    default:
      return "–";
  }
}

export function resolveCustomer(contract: MaintenanceContract): OptionEntry {
  const id = String(contract.hero_customer_id ?? "").trim();
  const label = maintenanceContractCustomerLabel(contract);
  if (id) return { key: `cid:${id}`, label: label || `Kunde #${id}` };
  if (label) return { key: `cname:${label.toLowerCase()}`, label };
  return {
    key: `contract:${contract.id ?? ""}`,
    label: contract.contract_number || `Vertrag #${contract.id || ""}`,
  };
}

export function resolveLocation(
  c: MaintenanceCampaign,
  contract: MaintenanceContract | null | undefined,
): CampaignLocationInfo {
  const subLocationName = String(c.sub_location_name || "").trim();
  const heroId = Number(c.hero_address_id);
  const subLocationIds = Array.isArray(c.sub_location_ids)
    ? c.sub_location_ids.filter((id) => Number(id) > 0)
    : [];
  const locations = Array.isArray(contract?.locations) ? contract!.locations!.filter(Boolean) : [];
  let baseLabel = "–";
  let heroAddressId: number | null = null;
  let key = "label:–";

  if (Number.isFinite(heroId) && heroId > 0) {
    const match = locations.find((l) => Number(l.hero_address_id) === heroId);
    baseLabel =
      contractLocationLabel(match, heroId) ||
      String(c.building_name || "").trim() ||
      String(c.location_name || "").trim() ||
      `Ort #${heroId}`;
    heroAddressId = heroId;
    key = `hero-address:${heroId}`;
  } else {
    const fallback = locations[0];
    if (fallback) {
      const fid = Number(fallback.hero_address_id);
      baseLabel = contractLocationLabel(fallback) || "–";
      heroAddressId = Number.isFinite(fid) && fid > 0 ? fid : null;
      key =
        Number.isFinite(fid) && fid > 0 ? `hero-address:${fid}` : `label:${baseLabel.toLowerCase()}`;
    } else {
      baseLabel =
        String(c.building_name || "").trim() || String(c.location_name || "–").trim() || "–";
      key = `label:${baseLabel.toLowerCase()}`;
    }
  }

  // Unterort hat Vorrang für Anzeige/Filter — mehrere Kampagnen am selben Hero-Ort
  // (z. B. LOG, P1, UZ) sollen nicht alle denselben Vertrags-Standortnamen zeigen.
  if (subLocationName) {
    return {
      heroAddressId,
      subLocationIds,
      key: `sub-location:${subLocationName.toLowerCase()}`,
      label: subLocationName,
    };
  }
  return { heroAddressId, subLocationIds, key, label: baseLabel };
}

export function resolveCategory(c: MaintenanceCampaign): OptionEntry {
  const key = String(c.maintenance_type_key || "").trim();
  const name = String(c.maintenance_type_name || "").trim();
  if (key) return { key: `type:${key}`, label: name || key };
  if (name) return { key: `name:${name.toLowerCase()}`, label: name };
  return { key: FILTER_ALL, label: "Unbekannte Kategorie" };
}

export function campaignIdOf(c: MaintenanceCampaign): number | null {
  const id = Number(c.campaign_id ?? c.id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function contractRelationLabel(contract: MaintenanceContract | null | undefined): string {
  return isOnCallContract(contract) ? "Auf Zuruf" : "Vertrag";
}

export function contractDisplayLabel(contract: MaintenanceContract | null | undefined): string {
  if (!contract) return "Vertrag";
  if (isOnCallContract(contract)) {
    return String(contract.name || "").trim() || "Auf Zuruf";
  }
  return (
    String(contract.contract_number || "").trim() ||
    String(contract.name || "").trim() ||
    (contract.id ? `Vertrag #${contract.id}` : "Vertrag")
  );
}

/** Link in die Wartungshülle einer Kampagne (mit allem Kontext). */
export function buildHuelleHref(
  campaign: MaintenanceCampaign,
  contract: MaintenanceContract,
  location?: CampaignLocationInfo,
): string | null {
  const typeKey = String(campaign.maintenance_type_key || "").trim();
  const projectId = Number(campaign.project_id);
  const contractId = Number(contract.id);
  const campaignId = campaignIdOf(campaign);
  if (!typeKey || !(projectId > 0) || !(contractId > 0) || !campaignId) return null;
  const loc = location ?? resolveLocation(campaign, contract);
  const usp = new URLSearchParams();
  usp.set("type_key", typeKey);
  usp.set("project_id", String(projectId));
  usp.set("contract_id", String(contractId));
  usp.set("campaign_id", String(campaignId));
  if (loc.heroAddressId) usp.set("hero_address_id", String(loc.heroAddressId));
  if (loc.subLocationIds.length === 1) usp.set("sub_location_id", String(loc.subLocationIds[0]));
  const dateMode = String(campaign.date_mode || "").trim().toLowerCase();
  const dateValue = String(campaign.date_value || "").trim();
  if (["day", "month", "year"].includes(dateMode) && dateValue) {
    usp.set("date_mode", dateMode);
    usp.set("date_value", dateValue);
  }
  return `/wartungen/huelle-detail?${usp.toString()}`;
}

export function buildCampaignView(
  campaign: MaintenanceCampaign,
  contract: MaintenanceContract,
  index = 0,
): CampaignView {
  const location = resolveLocation(campaign, contract);
  const category = resolveCategory(campaign);
  const campaignId = campaignIdOf(campaign);
  const title = String(
    campaign.name || campaign.maintenance_type_name || campaign.maintenance_type_key || "Wartung",
  ).trim();
  return {
    id: `${contract.id}-${campaignId ?? index}`,
    campaignId,
    contract,
    campaign,
    title,
    customer: resolveCustomer(contract),
    location,
    category,
    year: campaignYear(campaign),
    scheduleLabel: formatSchedule(campaign.date_mode, campaign.date_value),
    workflow: projectWorkflowStatus({
      workflow_status: campaign.workflow_status,
      workflow_status_label: campaign.workflow_status_label,
      project_completed: campaign.project_completed,
    }),
    contractLabel: contractTitle(contract),
    contractRelationLabel: contractRelationLabel(contract),
    href: buildHuelleHref(campaign, contract, location),
  };
}

export function dedupeOptions(items: OptionEntry[]): OptionEntry[] {
  const map = new Map<string, OptionEntry>();
  for (const it of items) if (!map.has(it.key)) map.set(it.key, { key: it.key, label: it.label });
  return Array.from(map.values()).sort((a, b) =>
    a.label.localeCompare(b.label, "de", { sensitivity: "base", numeric: true }),
  );
}

/* ───────────────────────────── Verträge ───────────────────────────── */

export type ContractStatusKey = "draft" | "active" | "paused" | "ended" | "cancelled";

export function normalizeContractStatus(status?: string | null): ContractStatusKey {
  const s = String(status || "").trim().toLowerCase();
  if (s === "active" || s === "paused" || s === "ended" || s === "cancelled") return s;
  return "draft";
}

export const CONTRACT_STATUS_LABELS: Record<ContractStatusKey, string> = {
  draft: "Entwurf",
  active: "Aktiv",
  paused: "Pausiert",
  ended: "Beendet",
  cancelled: "Storniert",
};

export function contractStatusLabel(status?: string | null): string {
  return CONTRACT_STATUS_LABELS[normalizeContractStatus(status)];
}

export function contractStatusClass(status?: string | null): string {
  switch (normalizeContractStatus(status)) {
    case "active":
      return "bg-status-green/15 text-status-green";
    case "paused":
      return "bg-status-orange/15 text-status-orange";
    case "cancelled":
      return "bg-status-red/15 text-status-red";
    case "ended":
      return "bg-fill-secondary text-label-secondary";
    default:
      return "bg-fill-tertiary text-label-secondary";
  }
}

/** „jährlich“, „halbjährlich“, „alle 2 Jahre“ – unabhängig davon, ob 12 Monate oder 1 Jahr gespeichert ist. */
export function contractIntervalLabel(c: MaintenanceContract): string {
  return frequencyLabel(toPeriod(c.interval_value, c.interval_unit));
}

/** „verlängert sich automatisch um 1 Jahr · Kündigungsfrist 3 Monate“ oder „“ ohne Verlängerung. */
export function contractRenewalLabel(c: MaintenanceContract): string {
  if (!c.auto_renew_enabled) return "";
  const renewal = toPeriod(c.renewal_term_value, c.renewal_term_unit);
  const notice = toPeriod(c.notice_period_value, c.notice_period_unit);
  const parts = [`verlängert sich automatisch${renewal ? ` um ${durationLabel(renewal)}` : ""}`];
  if (notice) parts.push(`Kündigungsfrist ${durationLabel(notice)}`);
  return parts.join(" · ");
}

/** Link zur Vertragsseite (Wartungen des Vertrags). */
export function contractDetailHref(contract: MaintenanceContract | number): string {
  const id = typeof contract === "number" ? contract : Number(contract.id);
  return `/wartungen/vertrags-wartungen?contract_id=${encodeURIComponent(String(id))}`;
}

/** Link zum Bearbeitungsformular eines Vertrags. */
export function contractEditHref(contract: MaintenanceContract | number): string {
  const id = typeof contract === "number" ? contract : Number(contract.id);
  return `/wartungen/vertrag-form?id=${encodeURIComponent(String(id))}`;
}

/** Anzeigename: Name, sonst Vertragsnummer, bei Auf Zuruf sonst „Auf Zuruf“. */
export function contractTitle(c: MaintenanceContract | null | undefined): string {
  if (!c) return "Vertrag";
  const name = String(c.name || "").trim();
  if (name) return name;
  if (isOnCallContract(c)) return "Auf Zuruf";
  return contractDisplayLabel(c);
}

/** Zweitzeile unter dem Titel: Vertragsnummer, wenn sie nicht schon der Titel ist. */
export function contractSubtitle(c: MaintenanceContract | null | undefined): string {
  if (!c || isOnCallContract(c)) return "";
  const number = String(c.contract_number || "").trim();
  const title = contractTitle(c);
  if (number && number !== title) return `Nr. ${number}`;
  if (!number && c.id) return `Vertrag #${c.id}`;
  return "";
}

/** Laufzeit als Text: „01.01.2026 – 31.12.2027“, „ab 01.01.2026“, „unbefristet“. */
export function contractRuntimeLabel(c: MaintenanceContract): string {
  const start = c.start_date ? formatDate(c.start_date) : "";
  const end = c.end_date ? formatDate(c.end_date) : "";
  if (start && end) return `${start} – ${end}`;
  if (start) return `ab ${start}`;
  if (end) return `bis ${end}`;
  return "unbefristet";
}

/** Tage bis zum Laufzeitende (negativ = abgelaufen), `null` ohne Enddatum. */
export function contractDaysUntilEnd(c: MaintenanceContract, today: Date = new Date()): number | null {
  const raw = String(c.end_date || "").trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const end = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((end - now) / 86_400_000);
}

export function contractLocationLabels(c: MaintenanceContract): string[] {
  const locations = Array.isArray(c.locations) ? c.locations.filter(Boolean) : [];
  return locations
    .map((loc) => contractLocationLabel(loc, loc?.hero_address_id ?? null))
    .filter(Boolean);
}

export function contractCategoryLabels(c: MaintenanceContract): string[] {
  const types = Array.isArray(c.maintenance_types) ? c.maintenance_types : [];
  return types.map((t) => String(t.name || t.key || "").trim()).filter(Boolean);
}

/** Volltextsuche über Vertragsnummer, Name, Kunde, Orte, Kategorien und Status. */
export function contractMatchesQuery(c: MaintenanceContract, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    c.contract_number,
    c.name,
    maintenanceContractCustomerLabel(c),
    c.hero_customer_id,
    contractStatusLabel(c.status),
    ...contractLocationLabels(c),
    ...contractCategoryLabels(c),
  ]
    .filter(Boolean)
    .map((x) => String(x).toLowerCase())
    .join(" ");
  return q.split(/\s+/).every((term) => haystack.includes(term));
}
